import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, setDoc, deleteDoc, getDocs } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { isPushSupported, requestAndRegister, teardown, attachTapHandler } from '../services/push';

// localStorage flag mirrors the user's "New Releases" opt-in so we can decide
// whether to re-register on launch before any network/Firestore read.
const PREF_KEY = 'oplixi_new_releases_enabled';

const NotificationsContext = createContext();

// Store a device token under the user's account so the backend can target it.
// Token is the doc id (sanitized) so re-registering the same device is a no-op.
async function storeToken(uid, token) {
  if (!db || !uid || !token) return;
  const id = token.replace(/\//g, '_').slice(0, 1400);
  await setDoc(
    doc(db, 'users', uid, 'pushTokens', id),
    { token, platform: Capacitor.getPlatform(), newReleases: true, updatedAt: Date.now() },
    { merge: true },
  );
}

async function removeAllTokens(uid) {
  if (!db || !uid) return;
  const snap = await getDocs(collection(db, 'users', uid, 'pushTokens'));
  await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
}

export function NotificationsProvider({ children }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const supported = isPushSupported();
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(PREF_KEY) === 'true'; } catch { return false; }
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const isUser = !!currentUser && !currentUser.isGuest;

  // Tap-to-open: when a "new release" notification is tapped, deep-link to it.
  useEffect(() => {
    if (!supported) return undefined;
    let detach = () => {};
    attachTapHandler((movieId) => navigate(`/movie/${movieId}`)).then((fn) => { detach = fn; });
    return () => detach();
  }, [supported, navigate]);

  // Re-register on launch / login when the user has previously opted in —
  // device tokens rotate, so we refresh the stored token silently.
  useEffect(() => {
    if (!supported || !isUser || !enabled) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = await requestAndRegister();
        if (!cancelled && token) await storeToken(currentUser.uid, token);
      } catch {
        // Silent on auto-refresh; the explicit enable() surfaces errors.
      }
    })();
    return () => { cancelled = true; };
  }, [supported, isUser, enabled, currentUser]);

  const enable = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const token = await requestAndRegister();
      if (isUser && token) await storeToken(currentUser.uid, token);
      try { localStorage.setItem(PREF_KEY, 'true'); } catch { /* noop */ }
      setEnabled(true);
    } catch (e) {
      setError(
        e.code === 'permission-denied'
          ? 'Notifications are turned off for Oplixi. Enable them in your device Settings.'
          : 'Could not enable notifications. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }, [currentUser, isUser]);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      await teardown();
      if (isUser) await removeAllTokens(currentUser.uid);
      try { localStorage.setItem(PREF_KEY, 'false'); } catch { /* noop */ }
      setEnabled(false);
    } finally {
      setBusy(false);
    }
  }, [currentUser, isUser]);

  return (
    <NotificationsContext.Provider value={{ supported, enabled, busy, error, enable, disable }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within a NotificationsProvider');
  return ctx;
}
